import React, { useState, useEffect, useCallback } from 'react';
import { Star, ChevronRight, Eye, EyeOff, MessageSquarePlus, Quote } from 'lucide-react';
import axios from 'axios';
import StarRating from './StarRating';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfileReviewsInline = ({ userId, isOwnProfile, onOpenReviewsModal }) => {
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/users/${userId}/reviews-summary`, { withCredentials: true });
      setSummary(res.data);
    } catch (e) { /* silent */ }
  }, [userId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1 || !comment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/reviews`, {
        rating,
        comment: comment.trim(),
        profile_user_id: userId
      }, { withCredentials: true });
      setShowForm(false);
      setRating(0);
      setComment('');
      fetchSummary();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVisibility = async () => {
    setToggling(true);
    try {
      const res = await axios.patch(`${API}/api/users/${userId}/reviews-visibility`, {}, { withCredentials: true });
      setSummary(prev => ({ ...prev, show_reviews: res.data.show_reviews }));
    } catch (e) { /* silent */ }
    setToggling(false);
  };

  if (!summary) return null;

  const hasReviews = summary.count > 0;
  const isHidden = !summary.show_reviews;

  // Owner sees hidden state notice
  if (isHidden && !isOwnProfile) return null;

  return (
    <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-700/50" data-testid="profile-reviews-inline">

      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
            Avis
          </h3>
          {hasReviews && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/20 px-2.5 py-1 rounded-full">
                <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-bold text-yellow-700 dark:text-yellow-300">{summary.average.toFixed(1)}</span>
              </div>
              <button
                onClick={onOpenReviewsModal}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                data-testid="reviews-count-link"
              >
                {summary.count} avis
              </button>
            </div>
          )}
          {!hasReviews && (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">Aucun avis pour le moment</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isOwnProfile && (
            <button
              onClick={toggleVisibility}
              disabled={toggling}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title={isHidden ? 'Afficher les avis' : 'Masquer les avis'}
              data-testid="toggle-reviews-visibility"
            >
              {isHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {isHidden ? 'Masqué' : 'Visible'}
            </button>
          )}
          {summary.can_review && !summary.has_reviewed && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 bg-purple-50 dark:bg-purple-900/20 px-3 py-1.5 rounded-full transition-all hover:bg-purple-100 dark:hover:bg-purple-900/30"
              data-testid="leave-review-btn"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              Laisser un avis
            </button>
          )}
        </div>
      </div>

      {/* Hidden state banner for owner */}
      {isOwnProfile && isHidden && (
        <div className="mb-3 p-2.5 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-100 dark:border-amber-800/30">
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Vos avis sont masqués pour les visiteurs. Cliquez sur "Masqué" pour les rendre visibles.
          </p>
        </div>
      )}

      {/* Latest testimonials */}
      {hasReviews && !isHidden && summary.latest && summary.latest.length > 0 && (
        <div className="space-y-2.5">
          {summary.latest.slice(0, 2).map((review) => (
            <div
              key={review.review_id}
              className="group relative flex gap-3 p-3 rounded-xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/30 hover:border-purple-200 dark:hover:border-purple-800/40 transition-all"
              data-testid={`review-card-${review.review_id}`}
            >
              <Quote className="absolute top-2 right-2.5 h-4 w-4 text-gray-200 dark:text-gray-700 opacity-60" />
              <img
                src={review.reviewer?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.reviewer?.display_name || '?')}&background=random`}
                alt=""
                className="h-8 w-8 rounded-full object-cover ring-2 ring-white dark:ring-gray-800 flex-shrink-0 mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                    {review.reviewer?.display_name || 'Utilisateur'}
                  </span>
                  <StarRating rating={review.rating} size="sm" />
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                  {review.comment}
                </p>
              </div>
            </div>
          ))}

          {summary.count > 2 && (
            <button
              onClick={onOpenReviewsModal}
              className="flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 transition-colors ml-1"
              data-testid="see-all-reviews-btn"
            >
              Voir les {summary.count} avis
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Inline review form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mt-3 p-4 rounded-xl bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/30" data-testid="inline-review-form">
          <div className="mb-3">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Votre note</label>
            <StarRating rating={rating} interactive onChange={setRating} size="lg" />
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Partagez votre experience..."
            className="w-full text-sm p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-purple-700 focus:border-transparent outline-none"
            rows={3}
            data-testid="review-comment-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setRating(0); setComment(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-1.5"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || rating < 1 || !comment.trim()}
              className="text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-1.5 rounded-lg transition-colors"
              data-testid="submit-review-btn"
            >
              {submitting ? 'Envoi...' : 'Publier'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ProfileReviewsInline;
